/**
 * components/consent/termsConsentItems.ts
 *
 * What the consent screen asks, as clause 2.1 of the general terms asks it at
 * account creation, by the keys the service stores
 * (`TERMS_CONSENT_ITEMS` in `optivo-service/src/services/core/termsConsent.ts`,
 * held together by `termsConsentItems.parity.test.ts`), and the company-ID
 * check the service makes, so the screen says a mistyped number before it is
 * sent.
 */

/** The business declaration (2.1.2), the terms and annexes (2.1.3), and each provision of 2.1.4. */
export const TERMS_CONSENT_ITEMS = [
    "business",
    "terms",
    "serviceNature",
    "pricePush",
    "automatedTools",
    "anonymisedData",
    "knowHowPenalty",
    "liability",
] as const;

export type TermsConsentItem = (typeof TERMS_CONSENT_ITEMS)[number];

/** The provisions clause 2.1.4 asks to be accepted separately and expressly. */
export const TERMS_EXPRESS_PROVISIONS = [
    "serviceNature",
    "pricePush",
    "automatedTools",
    "anonymisedData",
    "knowHowPenalty",
    "liability",
] as const satisfies readonly TermsConsentItem[];

/** The company ID as the service stores it: spaces out, leading zeros put back. */
export function normalizeCompanyId(value: string): string {
    const compact = value.replace(/\s+/g, "");
    return /^\d{1,8}$/.test(compact) ? compact.padStart(8, "0") : compact;
}

/** The service's check digit rule, body for body (`isValidCompanyId`). */
export function isValidCompanyId(value: string): boolean {
    if (!/^\d{8}$/.test(value)) return false;
    const digits = [...value].map(Number);
    const sum = digits.slice(0, 7).reduce((total, digit, index) => total + digit * (8 - index), 0);
    const remainder = sum % 11;
    const check = remainder === 0 ? 1 : remainder === 1 ? 0 : 11 - remainder;
    return digits[7] === check;
}
